import { translateKey } from 'src/locales/translate';
import {
  CreateWarehouseProductDto,
  UpdateWarehouseProductDto,
  WarehouseDispenseMode,
  WarehouseControlledCategory,
  WarehouseProductDetailDto,
  WarehouseProductKind,
  WarehouseProductPrefillDto,
  WarehouseQuantityMeasure,
  WarehouseUnit,
  WarehouseVatRate,
} from 'woofwoof-api';
import * as yup from 'yup';
import {
  WAREHOUSE_MAX_MONEY_MINOR,
  minorToInput,
  multiplyDivideHalfUp,
  parseMoneyToMinor,
} from '../../utils/money';
import { formatMilli, parseQuantityInput } from '../../utils/quantity';
import {
  CONTROLLED_CATEGORIES,
  DISPENSE_MODES,
  PRODUCT_KINDS,
  QUANTITY_MEASURES,
  VAT_RATES,
  WAREHOUSE_UNITS,
} from './warehouseProductForm.constants';

export interface WarehouseProductFormValues {
  kind: WarehouseProductKind;
  name: string;
  medicineProductId: string;
  isRx: boolean;
  controlledCategory: '' | WarehouseControlledCategory;
  unit: WarehouseUnit;
  unitCustom: string;
  unitsPerPackage: string;
  /** DRUG only (#692 rev B): whole packages or exact Units for a drug handed over. */
  dispenseMode: WarehouseDispenseMode;
  packagePriceGross: string;
  unitPriceGross: string;
  vatRate: WarehouseVatRate;
  minStockQuantity: string;
  minStockMeasure: WarehouseQuantityMeasure;
  marketingAuthorisationNumber: string;
  marketingAuthorisationHolder: string;
  withdrawalPeriod: string;
  form: string;
  strength: string;
  gtin: string;
}

export const DEFAULT_PRODUCT_FORM_VALUES: WarehouseProductFormValues = {
  kind: 'DRUG',
  name: '',
  medicineProductId: '',
  isRx: false,
  controlledCategory: '',
  unit: 'TABLET',
  unitCustom: '',
  unitsPerPackage: '1',
  dispenseMode: 'PACKAGES',
  packagePriceGross: '',
  unitPriceGross: '',
  vatRate: '8',
  minStockQuantity: '',
  minStockMeasure: 'PACKAGE',
  marketingAuthorisationNumber: '',
  marketingAuthorisationHolder: '',
  withdrawalPeriod: '',
  form: '',
  strength: '',
  gtin: '',
};

const message = (key: string): string =>
  translateKey(`warehouse.products.validation.${key}`);

/** Backend `@Max(100000)` on `unitsPerPackage`. */
const MAX_UNITS_PER_PACKAGE = 100000;
const costTooHigh = (): string =>
  translateKey('warehouse.documents.lines.costTooHigh');

/** Mirror of 422 MA_HOLDER_REQUIRED (review D4): an Rx drug needs both legal header fields. */
function requiredForRx(
  this: yup.TestContext,
  value: string | undefined,
): boolean {
  const { kind, isRx, controlledCategory } = this
    .parent as WarehouseProductFormValues;
  const isRxDrug = kind === 'DRUG' && (isRx || Boolean(controlledCategory));
  return !isRxDrug || Boolean(value?.trim());
}

export const warehouseProductFormSchema: yup.ObjectSchema<WarehouseProductFormValues> =
  yup.object({
    kind: yup.mixed<WarehouseProductKind>().oneOf(PRODUCT_KINDS).required(),
    name: yup
      .string()
      .trim()
      .required(message('nameRequired'))
      .max(200, message('nameTooLong')),
    medicineProductId: yup.string().defined(),
    isRx: yup.boolean().defined(),
    controlledCategory: yup
      .mixed<'' | WarehouseControlledCategory>()
      .oneOf(['', ...CONTROLLED_CATEGORIES])
      .defined(),
    unit: yup.mixed<WarehouseUnit>().oneOf(WAREHOUSE_UNITS).required(),
    unitCustom: yup
      .string()
      .defined()
      .max(50, message('unitCustomTooLong'))
      .test(
        'unit-custom',
        message('unitCustomRequired'),
        function hasCustomName(value) {
          return this.parent.unit !== 'OTHER' || Boolean(value?.trim());
        },
      ),
    unitsPerPackage: yup
      .string()
      .defined()
      .matches(/^[1-9]\d{0,5}$/, message('unitsPerPackage'))
      .test(
        'max',
        message('unitsPerPackage'),
        (value) => Number(value) <= MAX_UNITS_PER_PACKAGE,
      ),
    dispenseMode: yup
      .mixed<WarehouseDispenseMode>()
      .oneOf(DISPENSE_MODES)
      .required(),
    packagePriceGross: yup
      .string()
      .defined()
      .test(
        'price',
        message('price'),
        (value) => parseMoneyToMinor(value ?? '') !== null,
      )
      .test(
        'price-limit',
        costTooHigh(),
        (value) =>
          (parseMoneyToMinor(value ?? '') ?? 0) <= WAREHOUSE_MAX_MONEY_MINOR,
      ),
    unitPriceGross: yup
      .string()
      .defined()
      .test(
        'price',
        message('price'),
        (value) => !value?.trim() || parseMoneyToMinor(value) !== null,
      )
      .test(
        'price-limit',
        costTooHigh(),
        (value) =>
          !value?.trim() ||
          (parseMoneyToMinor(value) ?? 0) <= WAREHOUSE_MAX_MONEY_MINOR,
      ),
    vatRate: yup.mixed<WarehouseVatRate>().oneOf(VAT_RATES).required(),
    minStockQuantity: yup
      .string()
      .defined()
      .test(
        'quantity',
        message('quantity'),
        (value) => !value?.trim() || parseQuantityInput(value) !== null,
      ),
    minStockMeasure: yup
      .mixed<WarehouseQuantityMeasure>()
      .oneOf(QUANTITY_MEASURES)
      .required(),
    marketingAuthorisationNumber: yup
      .string()
      .defined()
      .max(100, message('marketingAuthorisationNumberTooLong'))
      .test('rx-facts', message('rxFactsRequired'), requiredForRx),
    marketingAuthorisationHolder: yup
      .string()
      .defined()
      .max(300, message('marketingAuthorisationHolderTooLong'))
      .test('rx-facts', message('rxFactsRequired'), requiredForRx),
    withdrawalPeriod: yup
      .string()
      .defined()
      .max(500, message('withdrawalPeriodTooLong')),
    form: yup.string().defined().max(200, message('formTooLong')),
    strength: yup.string().defined().max(200, message('strengthTooLong')),
    gtin: yup
      .string()
      .defined()
      .test(
        'gtin',
        message('gtin'),
        (value) => !value?.trim() || /^\d{8,14}$/.test(value.trim()),
      ),
  });

const optional = (value: string): string | undefined =>
  value.trim() || undefined;

export function buildProductFields(
  values: WarehouseProductFormValues,
): UpdateWarehouseProductDto {
  const isDrug = values.kind === 'DRUG';
  const unitPrice = optional(values.unitPriceGross);
  const minStock = optional(values.minStockQuantity);
  const drugText = (value: string): string | undefined =>
    isDrug ? optional(value) : undefined;
  const fields: UpdateWarehouseProductDto = {
    name: values.name.trim(),
    unit: values.unit,
    unitsPerPackage: Number(values.unitsPerPackage),
    packagePriceGrossMinor: parseMoneyToMinor(values.packagePriceGross) ?? 0,
    vatRate: values.vatRate,
  };
  const medicineProductId = drugText(values.medicineProductId);
  const marketingAuthorisationNumber = drugText(
    values.marketingAuthorisationNumber,
  );
  const marketingAuthorisationHolder = drugText(
    values.marketingAuthorisationHolder,
  );
  const withdrawalPeriod = drugText(values.withdrawalPeriod);
  const form = drugText(values.form);
  const strength = drugText(values.strength);
  const gtin = optional(values.gtin);
  return {
    ...(medicineProductId ? { medicineProductId } : {}),
    ...(isDrug
      ? { isRx: values.isRx || Boolean(values.controlledCategory) }
      : {}),
    // PUT replaces every field: a drug always says how it is handed over.
    ...(isDrug ? { dispenseMode: values.dispenseMode } : {}),
    ...(isDrug && values.controlledCategory
      ? { controlledCategory: values.controlledCategory }
      : {}),
    ...fields,
    ...(values.unit === 'OTHER'
      ? { unitCustom: values.unitCustom.trim() }
      : {}),
    ...(unitPrice
      ? { unitPriceGrossMinor: parseMoneyToMinor(unitPrice) ?? undefined }
      : {}),
    ...(minStock
      ? {
          minStockQuantity: parseQuantityInput(minStock) ?? undefined,
          minStockMeasure: values.minStockMeasure,
        }
      : {}),
    ...(marketingAuthorisationNumber ? { marketingAuthorisationNumber } : {}),
    ...(marketingAuthorisationHolder ? { marketingAuthorisationHolder } : {}),
    ...(withdrawalPeriod ? { withdrawalPeriod } : {}),
    ...(form ? { form } : {}),
    ...(strength ? { strength } : {}),
    ...(gtin ? { gtin } : {}),
  };
}

export function buildCreateProductPayload(
  values: WarehouseProductFormValues,
): CreateWarehouseProductDto {
  return { kind: values.kind, ...buildProductFields(values) };
}

export function productToFormValues(
  product?: WarehouseProductDetailDto | null,
  initialName = '',
): WarehouseProductFormValues {
  if (!product) {
    return { ...DEFAULT_PRODUCT_FORM_VALUES, name: initialName };
  }
  const packageMilli = product.unitsPerPackage * 1000;
  const minInPackages =
    product.minStockMilli !== undefined &&
    product.unitsPerPackage > 1 &&
    product.minStockMilli % packageMilli === 0;
  // Mirror of the backend `unitPriceFromPackage` (FW-2/2): half-up rounding
  // of exact integers, never a float divide.
  const derivedUnitPrice =
    multiplyDivideHalfUp(
      product.packagePriceGrossMinor,
      1,
      product.unitsPerPackage,
    ) ?? 0;
  return {
    kind: product.kind,
    name: product.name,
    medicineProductId: product.medicineProductId ?? '',
    isRx: product.isRx,
    controlledCategory: product.controlledCategory ?? '',
    unit: product.unit,
    unitCustom: product.unitCustom ?? '',
    unitsPerPackage: String(product.unitsPerPackage),
    dispenseMode: product.dispenseMode ?? 'PACKAGES',
    packagePriceGross: minorToInput(product.packagePriceGrossMinor),
    unitPriceGross:
      product.unitPriceGrossMinor === derivedUnitPrice
        ? ''
        : minorToInput(product.unitPriceGrossMinor),
    vatRate: product.vatRate,
    minStockQuantity:
      product.minStockMilli === undefined
        ? ''
        : minInPackages
        ? String(product.minStockMilli / packageMilli)
        : formatMilli(product.minStockMilli),
    minStockMeasure: minInPackages ? 'PACKAGE' : 'UNIT',
    marketingAuthorisationNumber: product.marketingAuthorisationNumber ?? '',
    marketingAuthorisationHolder: product.marketingAuthorisationHolder ?? '',
    withdrawalPeriod: product.withdrawalPeriod ?? '',
    form: product.form ?? '',
    strength: product.strength ?? '',
    gtin: product.gtin ?? '',
  };
}

/** The registry never decides the controlled category; it only hints at it. */
export function applyPrefill(
  values: WarehouseProductFormValues,
  prefill: WarehouseProductPrefillDto,
): WarehouseProductFormValues {
  return {
    ...values,
    kind: 'DRUG',
    name: prefill.name,
    medicineProductId: prefill.medicineProductId,
    isRx: prefill.isRx,
    form: prefill.form ?? '',
    strength: prefill.strength ?? '',
    marketingAuthorisationNumber: prefill.marketingAuthorisationNumber ?? '',
    marketingAuthorisationHolder: prefill.marketingAuthorisationHolder ?? '',
    withdrawalPeriod: prefill.withdrawalPeriod ?? '',
    gtin: prefill.gtin ?? values.gtin,
  };
}
